/**
 * The four critical-value tables — z, t, χ² and F — generated from the distribution
 * functions rather than typed in, so every entry is correct to the digits shown and the
 * lookups beside each table agree with it exactly.
 */

import {
  chiSquareQuantile,
  fQuantile,
  normalCdf,
  normalQuantile,
  normalUpper,
  studentTQuantile,
} from "@/lib/stats/distributions";

/* --------------------------------------------------------------------- z */

export type ZArea = "left" | "right" | "center";

/** Hundredths added along a z-table row. */
export const Z_COLUMNS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

/** Rows in tenths: 34 is z = 3.4. The negative table runs −3.4 … −0.0. */
export const Z_ROW_TENTHS = Array.from({ length: 35 }, (_, i) => i);

/** The z a cell stands for, in hundredths: row −1.9 and column .06 is −1.96. */
export function zCellHundredths(
  rowTenths: number,
  column: number,
  negative: boolean
): number {
  const magnitude = rowTenths * 10 + column;
  return negative ? -magnitude : magnitude;
}

/** The area the table prints for z, by table type. */
export function zArea(z: number, area: ZArea): number {
  if (area === "left") {
    return normalCdf(z);
  }
  if (area === "right") {
    return normalUpper(z);
  }
  // Mean to z: the area between 0 and |z|.
  return 0.5 - normalUpper(Math.abs(z));
}

/** The z whose tabulated area is `p`; for "center", the positive z. */
export function zFromArea(p: number, area: ZArea): number | null {
  if (area === "center") {
    return p > 0 && p < 0.5 ? -normalQuantile(0.5 - p) : null;
  }
  if (!(p > 0 && p < 1)) {
    return null;
  }
  return area === "left" ? normalQuantile(p) : -normalQuantile(p);
}

/** Where a z with two decimals sits in the table, or null when it is off the table. */
export function zCell(
  z: number
): { rowTenths: number; column: number; negative: boolean } | null {
  const hundredths = Math.round(Math.abs(z) * 100);
  if (hundredths > 349 || Math.abs(Math.abs(z) * 100 - hundredths) > 1e-6) {
    return null;
  }
  return {
    rowTenths: Math.floor(hundredths / 10),
    column: hundredths % 10,
    // −0.00 is printed in the positive table.
    negative: z < 0 && hundredths > 0,
  };
}

/* --------------------------------------------------------------------- t */

/** One-tailed α across the top of the t-table; two-tailed is double. */
export const T_ALPHAS = [
  0.25, 0.1, 0.05, 0.025, 0.01, 0.005, 0.001, 0.0005,
] as const;

const T_DF_TAIL = [40, 50, 60, 80, 100, 120, 1000, Number.POSITIVE_INFINITY];

export const T_DF_ROWS: readonly number[] = [
  ...Array.from({ length: 30 }, (_, i) => i + 1),
  ...T_DF_TAIL,
];

/** The t that cuts off `alpha` in the upper tail. Infinite df is the normal. */
export function tCritical(df: number, alpha: number): number {
  if (df === Number.POSITIVE_INFINITY) {
    return normalQuantile(1 - alpha);
  }
  return studentTQuantile(1 - alpha, df);
}

/* -------------------------------------------------------------------- χ² */

/** Right-tail areas across the top of the χ² table. */
export const CHI2_ALPHAS = [
  0.995, 0.99, 0.975, 0.95, 0.9, 0.1, 0.05, 0.025, 0.01, 0.005,
] as const;

export const CHI2_DF_ROWS: readonly number[] = [
  ...Array.from({ length: 30 }, (_, i) => i + 1),
  40,
  50,
  60,
  70,
  80,
  90,
  100,
];

/** The χ² value with `alpha` of the area to its right. */
export function chiSquareCritical(df: number, alpha: number): number {
  return chiSquareQuantile(1 - alpha, df);
}

/* --------------------------------------------------------------------- F */

export const F_ALPHAS = [0.1, 0.05, 0.025, 0.01] as const;

const INFINITE = Number.POSITIVE_INFINITY;

export const F_DF1: readonly number[] = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  12,
  15,
  20,
  24,
  30,
  40,
  60,
  120,
  INFINITE,
];

export const F_DF2: readonly number[] = [
  ...Array.from({ length: 30 }, (_, i) => i + 1),
  40,
  60,
  120,
  INFINITE,
];

/**
 * The F value with `alpha` of the area to its right.
 *
 * Infinite df are the exact limits, not a large stand-in: as d₂ → ∞, F(d₁, d₂) → χ²(d₁)/d₁;
 * as d₁ → ∞, F → d₂/χ²(d₂), whose upper α point is d₂ over the lower α point of χ²(d₂).
 */
export function fCritical(df1: number, df2: number, alpha: number): number {
  const infinite1 = df1 === INFINITE;
  const infinite2 = df2 === INFINITE;
  if (infinite1 && infinite2) {
    return 1;
  }
  if (infinite2) {
    return chiSquareQuantile(1 - alpha, df1) / df1;
  }
  if (infinite1) {
    return df2 / chiSquareQuantile(alpha, df2);
  }
  return fQuantile(1 - alpha, df1, df2);
}

/** Left-tail F: the value with `alpha` of the area to its LEFT, via F(a; d₁, d₂) = 1/F(1 − a; d₂, d₁). */
export function fLowerCritical(
  df1: number,
  df2: number,
  alpha: number
): number {
  return 1 / fCritical(df2, df1, alpha);
}

/* ---------------------------------------------------------------- tails */

export type CriticalTail = "right" | "left" | "two";

/** A positive α below 1 — the domain every lookup accepts. */
export function isValidAlpha(alpha: number): boolean {
  return Number.isFinite(alpha) && alpha > 0 && alpha < 1;
}

/** A positive df; an empty field or 0 is not one. Fractional df are allowed. */
export function isValidDf(df: number): boolean {
  return Number.isFinite(df) && df > 0;
}
